import { createDrawer, Drawer } from '@nexelem/drawer';
import { useGraphneResourcesGroupTimeframe } from '../../../../../queryHooks/productionPlanHooks';
import { Badge, Button, Status, Skeleton } from '@nexelem/components';
import { useCustomTranslation } from '../../../../../hooks';
import { DrawerHeader } from './DrawerHeader';
import { useState } from 'react';
import { DateRange, ShiftSelector } from './ShiftSelector';
import { DrawerWorker } from './DrawerWorker';
import { useSelectGanttResourceArea } from './useSelectGanttResourceArea';
import emptyState from '../../../../../../assets/empty-state.svg';
import { useDelayedState } from '@nexelem/utils';
import { SearchChip } from '@nexelem/inputs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faUserCheck, faUserSlash } from '@fortawesome/pro-light-svg-icons';

const BrigadeDrawer = () => {
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const { t, tWrapper } = useCustomTranslation('products.productionPlan');
  const { areas, areaId, setAreaId } = useSelectGanttResourceArea();
  const [search, delayedSearch, setSearch] = useDelayedState('');

  const { data: workers, isLoading } = useGraphneResourcesGroupTimeframe(
    {
      resourceGroupId: areaId?.toString() || '',
      from: dateRange?.from,
      to: dateRange?.to,
      search: delayedSearch,
    },
    !!dateRange || !!areaId,
  );

  if (!areas || !areaId) {
    return (
      <Drawer>
        <div className="flex h-full flex-col items-center justify-center gap-4">
          <div
            className="h-70 w-70 bg-contain bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${emptyState})` }}
          />
          <span className="text-s18">{tWrapper('noAreasFound')}</span>
          <Button StartIcon={<FontAwesomeIcon icon={faCircleXmark} />} variant="outlined" onClick={closeBrigadeDrawer}>
            {t('common.close')}
          </Button>
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer>
      <DrawerHeader areaId={areaId} areas={areas} setAreaId={setAreaId} />
      <div className="px-6 py-4">
        <ShiftSelector onChange={setDateRange} />
        <div className="mt-4 flex justify-between">
          <span className="text-t16-sb">{tWrapper('shiftWorkerList')}</span>
          <div className="flex h-6.5 gap-2">
            {!!workers?.available && (
              <Badge StartIcon={<FontAwesomeIcon icon={faUserCheck} />}>{workers.available}</Badge>
            )}
            {!!workers?.unavailable.toString() && (
              <Status
                color="red"
                Icon={<FontAwesomeIcon className="-mr-1 ml-2" icon={faUserSlash} />}
                text={workers.unavailable.toString()}
                variant="contained"
              />
            )}
          </div>
        </div>
        <SearchChip
          className="mt-4"
          placeholder={t('workers.competenceMatrix.filters.comboSearch')}
          value={search}
          onChange={setSearch}
        />
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2 px-6">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
      ) : (
        <div className="scroll-stable ml-6 overflow-auto pr-2.5 pb-8">
          {workers?.resources?.map((view) => (
            <DrawerWorker data={view} dateRange={dateRange} key={view.resource.id} />
          ))}

          {workers?.resources?.length === 0 && (
            <div className="mt-8 text-center text-s18">{tWrapper('noWorkersAvailable')}</div>
          )}
        </div>
      )}
    </Drawer>
  );
};

export const { toggleBrigadeDrawer, closeBrigadeDrawer } = createDrawer(BrigadeDrawer, {
  name: 'BrigadeDrawer',
  width: 420,
  slot: 'left',
  drawerId: () => 'production-plan-brigade-drawer',
});
